import type { Square } from '../game/types';

export type LandingKind = 'normal' | 'capture' | 'check' | 'terminal';

export interface PieceMotionSnapshot {
  selectedSquare: Square | null;
  selectedLift: number;
  landing: null | {
    square: Square;
    kind: LandingKind;
    progress: number;
    ripple: number;
  };
  animating: boolean;
}

const SELECT_DURATION_MS = 90;
const LANDING_DURATION_MS = 140;
const TACTICAL_DURATION_MS = 260;

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

export class PieceMotionState {
  private selectedSquare: Square | null = null;
  private selectedAt: number | null = null;
  private landingState: { square: Square; kind: LandingKind; startedAt: number } | null = null;

  constructor(
    private readonly now: () => number = () => performance.now(),
    private readonly reducedMotion = false,
  ) {}

  select(square: Square | null): void {
    this.selectedSquare = square;
    this.selectedAt = square === null ? null : this.now();
  }

  land(square: Square, kind: LandingKind): void {
    this.selectedSquare = null;
    this.selectedAt = null;
    this.landingState = this.reducedMotion
      ? null
      : { square, kind, startedAt: this.now() };
  }

  reset(): void {
    this.selectedSquare = null;
    this.selectedAt = null;
    this.landingState = null;
  }

  snapshot(): PieceMotionSnapshot {
    const now = this.now();
    const selectedLift = this.selectedSquare === null || this.reducedMotion || this.selectedAt === null
      ? 0
      : clamp01((now - this.selectedAt) / SELECT_DURATION_MS);

    let landing: PieceMotionSnapshot['landing'] = null;
    if (this.landingState) {
      const elapsed = Math.max(0, now - this.landingState.startedAt);
      const duration = this.landingState.kind === 'normal'
        ? LANDING_DURATION_MS
        : TACTICAL_DURATION_MS;
      if (elapsed <= duration) {
        const rippleProgress = clamp01(elapsed / TACTICAL_DURATION_MS);
        landing = {
          square: this.landingState.square,
          kind: this.landingState.kind,
          progress: clamp01(elapsed / LANDING_DURATION_MS),
          ripple: this.landingState.kind === 'normal'
            ? 0
            : Math.sin(Math.PI * rippleProgress),
        };
      } else {
        this.landingState = null;
      }
    }

    return {
      selectedSquare: this.selectedSquare,
      selectedLift,
      landing,
      animating: (!this.reducedMotion && this.selectedSquare !== null && selectedLift < 1)
        || landing !== null,
    };
  }
}

export function dragDepthScale(y: number, boardHeight: number): number {
  if (boardHeight <= 0) return 1;
  return 0.96 + clamp01(y / boardHeight) * 0.08;
}

export function classifyLandingEffect(input: {
  terminal: boolean;
  givesCheck: boolean;
  capture: boolean;
}): LandingKind {
  if (input.terminal) return 'terminal';
  if (input.givesCheck) return 'check';
  if (input.capture) return 'capture';
  return 'normal';
}
